import { Component } from '@angular/core'
import { NuclearObservable } from '../nuclear-observable'
import { BaseComponent } from './base.component'
import { Account, Customer, CouponApplication, Coupon, Subscription } from '../types'
import { customerGetters } from '../stores/customer-store'
import { CustomerActions } from '../actions/customer-actions'
import moment from 'moment'
import {
  max,
  find,
  get,
  some,
  sortBy
} from 'lodash-es'
import { sortedCustomers } from '../utils'
import { prettyDollars, prettyCents, discountedCents } from '../logic/pricing'

@Component({
  selector: 'account-activation-summary',
  template: `
  <div *ngIf="componentReady()">

    <div class="small-bottom-buffer">
      The following is a recurring charge to your credit card when your subscription renews:
    </div>

    <div class="table-responsive">
      <table class="table borderless">
        <ng-template ngFor let-c [ngForOf]="sortedCustomers(customersWithSubscriptions())">
          <tr class="top-space" [class.bottom-border]="usableAndFutureCouponApplications(c).length === 0">
            <td>
              <div class="biggish-text">
                <strong>{{c.businessName}}</strong>
              </div>
              <div class="small-text">
                {{concisePlanName(c.subscription)}} {{c.subscription.quantity + ' post' + (c.subscription.quantity != 1 ? 's' : '') + ' per month'}}
              </div>
              <div class="smaller-text">
                <strong>{{c.subscription.plan?.words?.toLocaleString()}} words</strong>
              </div>
            </td>

            <td class="text-right">
              <strong class="account-activation-price">
                <span class="currency">$</span>
                <span class="dollars">{{dollars(c.subscription.totalPrice)}}</span>
                <span class="cents">{{cents(c.subscription.totalPrice)}}</span>
                <span class="per-renewal">/per renewal</span>
              </strong>
            </td>
          </tr>

          <tr class="text-primary" [class.bottom-border]="i === usableAndFutureCouponApplications(c).length-1" *ngFor="let ca of usableAndFutureCouponApplications(c); let i = index">
            <td>
              <strong>{{couponDescription(ca.coupon)}}</strong>
              {{ca.coupon.externalName}}
              <div class="text-black" *ngIf="ca.expiresAt">
                Offer expires {{distanceOfTimeInWords(ca.expiresAt)}}
              </div>
            </td>

            <td class="text-right">
              <strong>
                <span *ngIf="ca.coupon.discountType == 'cents'">- {{ca.coupon.discountInDollars | currency:'USD':'symbol':'1.2-2'}}</span>
                <span *ngIf="ca.coupon.discountType == 'percent'">- {{ca.coupon.discountPercent}}%</span>
                <span *ngIf="ca.coupon.discountType == 'free_post'">- {{ca.coupon.quantity + 'free post' + (ca.coupon.quantity != 1 ? 's' : '')}}</span>
              </strong>
            </td>
          </tr>
        </ng-template>

        <tr *ngIf="customersWithSubscriptions()?.length > 0">
          <td>
            <strong>Recurring Total</strong>
          </td>
          <td class="text-right">
            <strong class="account-activation-price">
              <span class="currency">$</span>
              <span class="dollars">{{dollars(discountedTotal(customers))}}</span>
              <span class="cents">{{cents(discountedTotal(customers))}}</span>
              <span class="per-renewal">/per renewal</span>
            </strong>
          </td>
        </tr>
      </table>
    </div>
    <div *ngIf="customersWithFreePosts().length > 0">
      <strong>You have a free post coupon!</strong>
      <div *ngFor="let c of customersWithFreePosts()">
        <strong>{{ c.businessName }}:</strong>
        {{ c.unusedFreePostCouponCount + ' free post' + (c.unusedFreePostCouponCount > 1 ? 's' : '') }}
      </div>
    </div>
    <div class="float-left top-buffer bottom-buffer">
      <em>
      30-day Money-Back Guarantee,
        <a target="_blank" href="/pages/tos/customer#refund">Terms and Conditions apply</a>
      </em>
    </div>
  </div>
  `,
  styles: [`
    tr.top-space td {
      padding-top: 10px;
    }
    tr.bottom-border td {
      border-bottom: 1px solid #dddddd;
      padding-bottom: 10px;
    }
  `]
})
export class AccountActivationSummary extends BaseComponent {
  account: Account;
  customers: Customer[];

  constructor(CustomerActions: CustomerActions) {
    super()
    CustomerActions.loadCustomersWithSubscriptions()

    const accountObs = NuclearObservable.from<Account>(customerGetters.account, { toJS: true }).subscribe(a => this.account = a)
    const custObs = NuclearObservable.from<Customer[]>(customerGetters.customers, { toJS: true }).subscribe(cs => this.customers = cs)
    this.observers.push(accountObs, custObs)
  }

  concisePlanName(subscription: Subscription): string {
    const planAddOns = sortBy(get(subscription, 'planAddOns', []), 'code')
    const conciseName = get(subscription, ['plan', 'conciseName'], 'Content')
    return planAddOns.length >= 1 ? planAddOns.map(pao => pao.name).join(", ") : conciseName
  }

  componentReady(): boolean {
    return this.account && this.customers && this.customers.filter(c => c.subscription && c.subscription.plan).length > 0
  }

  customersWithSubscriptions(): Customer[] {
    if (this.customers)
      return this.customers.filter(c => !!c.subscription === true)
  }

  distanceOfTimeInWords(expiresAt: string): string {
    return moment(expiresAt).fromNow()
  }

  customersWithFreePosts(): Customer[] {
    return this.customers.filter(c => c.unusedFreePostCouponCount > 0)
  }

  findByCoupon(couponApplications: CouponApplication[], coupon: Coupon): CouponApplication {
    return find(couponApplications, ca => ca.coupon.id === coupon.id)
  }

  sortedCustomers(customers: Customer[]): Customer[] {
    return sortedCustomers(customers)
  }

  usableAndFutureCouponApplications(customer: Customer): CouponApplication[] {
    if (get(customer, 'subscription.plan.billingFrequency') === 'monthly' && customer.couponApplications) {
      return customer.couponApplications.filter(ca => ca.usable || ca.future).map(ca => {
        const subscription = customer.subscription
        if (ca.coupon.dollarLimit && ((subscription.totalPrice - subscription.totalPriceWithDiscount) > ca.coupon.dollarLimit * 100)) {
          ca.coupon.discountType = 'cents'
          ca.coupon.discountInCents = ca.coupon.dollarLimit * 100
          ca.coupon.discountInDollars = ca.coupon.dollarLimit
          ca.coupon.externalName = `Coupon maximum of $${ca.coupon.dollarLimit} reached`
        } else {
          const adjustedDiscountCents = subscription.totalPriceWithDiscount < 0 ? subscription.totalPrice : ca.coupon.discountInCents
          ca.coupon.discountInDollars = adjustedDiscountCents / 100
        }
        return ca
      })
    } else {
      return []
    }
  }

  couponDescription(c: Coupon): string {
    if (c.discountType === 'free_post') {
      return 'Free Post' + (c.quantity !== 1 ? 's' : '')
    } else {
      switch (c.duration) {
        case 'signup':
          return 'First Month Discount'
        case 'second_month':
          return 'Second Month Discount'
        case 'forever':
          return 'Recurring Discount'
        default:
          return ''
      }
    }
  }

  anyAnnualSubscriptions(customers: Customer[]): boolean {
    return some(customers, c => get(c, 'subscription.plan.billingFrequency') === 'annually')
  }

  dollars(price: number): string {
    return prettyDollars(price)
  }

  cents(price: number): string {
    return prettyCents(price)
  }

  discountedTotal(customers: Customer[]): number {
    let total = 0
    customers.forEach(c => {
      if (c.subscription) {
        const coupons = this.usableAndFutureCouponApplications(c).map(ca => ca.coupon)
        total += max([discountedCents(c.subscription.totalPrice, coupons), 0])
      }
    })
    return total
  }
}
